import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, MapPin, Globe, LayoutGrid, List as ListIcon, CalendarDays,
  ChevronLeft, ChevronRight, X, Loader2,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { InterestedAvatars } from "@/components/social/InterestedButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading, EmptyState } from "@/components/common/Loading";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useLocalMode } from "@/context/LocalModeContext";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";
import { eventDateParts, zoneLetterAbbr } from "@/lib/timeago";
import { MultiSelect } from "@/components/directory/MultiSelect";
import { GeoRadiusFilter, isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { DirectoryCreateButton } from "@/components/common/DirectoryCreateButton";
import { trackResult } from "@/lib/analytics";
import { useAnalyticsImpressions } from "@/hooks/useAnalyticsImpressions";

const pad = (n) => String(n).padStart(2, "0");

function DateBadge({ iso }) {
  // Event times are a wall clock in the event's own zone — never re-projected here.
  const p = eventDateParts(iso);
  return (
    <div className="flex h-16 w-14 shrink-0 flex-col items-center justify-center rounded-xl border border-border bg-card text-center">
      <span className="text-[11px] font-[640] uppercase text-primary">{p.month}</span>
      <span className="text-lg font-[700] leading-none text-foreground">{p.day}</span>
      <span className="mt-0.5 text-[10px] font-[560] uppercase leading-none text-muted-foreground" data-testid="date-badge-weekday">{p.weekday}</span>
    </div>
  );
}

/** "Nov 5 – Nov 7" when an event crosses days; empty string for single-day events. */
function dayRange(ev) {
  if (!ev.end_at || ev.end_at.slice(0, 10) === (ev.start_at || "").slice(0, 10)) return "";
  const s = eventDateParts(ev.start_at);
  const e = eventDateParts(ev.end_at);
  return `${s.month} ${s.day} – ${e.month} ${e.day}`;
}

function EventCard({ ev, references, trackRef, onTrackClick }) {
  const p = eventDateParts(ev.start_at);
  const range = dayRange(ev);
  // Human-readable zone: "MDT" where English has one, otherwise the city name.
  const zone = ev.timezone ? (labelFor(references.timezones, ev.timezone) || ev.timezone) : "";
  const tz = zone ? (zoneLetterAbbr(ev.start_at, zone) || zone) : "";
  return (
    <Link to={`/event/${ev.slug}`} data-testid={`event-public-${ev.id}`} ref={trackRef} onClick={onTrackClick}>
      <Card className="flex h-full flex-col overflow-hidden border-0 transition-shadow hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)]">
        <div className="h-32 bg-gradient-to-r from-teal-100 to-teal-50">
          {ev.cover_url && <img src={fileUrl(ev.cover_url)} alt="" className="h-full w-full object-cover" />}
        </div>
        <div className="flex flex-1 flex-col p-4">
          <div className="flex items-start gap-3">
            <DateBadge iso={ev.start_at} />
            <div className="min-w-0">
              <h3 className="line-clamp-2 font-[640] text-foreground">{ev.title}</h3>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                {ev.format === "virtual" ? <Globe className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                <span data-testid="event-card-time">
                  {ev.format === "virtual" ? "Online" : (ev.location?.city || "In person")} · {range ? `${range} · ` : ""}{p.time}{tz ? ` (${tz})` : ""}
                </span>
              </div>
            </div>
          </div>
          {ev.summary && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{ev.summary}</p>}
          <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
            {ev.category && <Badge className="bg-teal-50 text-teal-700">{labelFor(references.event_categories, ev.category)}</Badge>}
            {ev.source === "internal" && <Badge variant="secondary">Official</Badge>}
          </div>
          {ev.interested_preview && ev.interested_preview.count > 0 && (
            <InterestedAvatars avatars={ev.interested_preview.avatars} count={ev.interested_preview.count} className="mt-3" />
          )}
        </div>
      </Card>
    </Link>
  );
}

function MonthCalendar({ events, references }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  // Day overflow modal — when a day has >2 events, the "+N" chip opens this.
  // `dayModal` is either null or { key, label, events[] }.
  const [dayModal, setDayModal] = useState(null);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = (() => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; })();

  const byDay = useMemo(() => {
    const m = {};
    // A multi-day event belongs on EVERY day it runs, not just its start date.
    events.forEach((e) => {
      if (!e.start_at) return;
      const first = e.start_at.slice(0, 10);
      const last = (e.end_at || e.start_at).slice(0, 10);
      const [y, mo, da] = first.split("-").map(Number);
      const cursorDay = new Date(y, mo - 1, da);
      for (let i = 0; i < 400; i++) {
        const key = `${cursorDay.getFullYear()}-${pad(cursorDay.getMonth() + 1)}-${pad(cursorDay.getDate())}`;
        (m[key] = m[key] || []).push(e);
        if (key >= last) break;
        cursorDay.setDate(cursorDay.getDate() + 1);
      }
    });
    return m;
  }, [events]);

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const fmtDayLabel = (key) => {
    // key = YYYY-MM-DD; build a Date in the local tz for nice formatting.
    const [y, mo, da] = key.split("-").map(Number);
    return new Date(y, mo - 1, da).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  };

  return (
    <Card className="border-0 p-4" data-testid="events-calendar">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-[640] text-foreground">{cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}</h3>
        <div className="flex items-center gap-1">
          <Button variant="secondary" size="sm" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="secondary" size="sm" onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Today</Button>
          <Button variant="secondary" size="sm" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-[600] text-muted-foreground">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="min-h-[88px] rounded-lg bg-secondary/30" />;
          const key = `${year}-${pad(month + 1)}-${pad(d)}`;
          const dayEvents = byDay[key] || [];
          const isToday = key === todayKey;
          const overflow = dayEvents.length - 2;
          return (
            <div key={key} className={`min-h-[88px] rounded-lg border p-1.5 ${isToday ? "border-primary bg-teal-50/40" : "border-border"}`}>
              <div className={`text-xs font-[600] ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d}</div>
              <div className="mt-1 space-y-1">
                {dayEvents.slice(0, 2).map((e) => (
                  <Link key={e.id} to={`/event/${e.slug}`} className="block truncate rounded bg-teal-50 px-1.5 py-0.5 text-[11px] font-[540] text-teal-800 hover:bg-teal-100" title={e.title} data-testid={`cal-event-${e.id}`}>{e.title}</Link>
                ))}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={() => setDayModal({ key, label: fmtDayLabel(key), events: dayEvents })}
                    className="w-full rounded px-1.5 py-0.5 text-left text-[11px] font-[600] text-primary hover:bg-secondary"
                    data-testid={`cal-overflow-${key}`}
                    aria-label={`Show all ${dayEvents.length} events on ${fmtDayLabel(key)}`}
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={!!dayModal} onOpenChange={(open) => !open && setDayModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cal-day-modal">
          <DialogHeader>
            <DialogTitle>{dayModal?.label}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
            {(dayModal?.events || []).map((e) => {
              const p = eventDateParts(e.start_at);
              const ep = eventDateParts(e.end_at);
              const spans = Boolean(e.end_at && e.end_at.slice(0, 10) !== (e.start_at || "").slice(0, 10));
              const zone = e.timezone ? (labelFor(references?.timezones, e.timezone) || e.timezone) : "";
              const tz = zone ? (zoneLetterAbbr(e.start_at, zone) || zone) : "";
              return (
                <Link
                  key={e.id}
                  to={`/event/${e.slug}`}
                  onClick={() => setDayModal(null)}
                  className="flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-secondary"
                  data-testid={`cal-day-modal-event-${e.id}`}
                >
                  <DateBadge iso={e.start_at} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-[600] text-foreground">{e.title}</div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground" data-testid="cal-modal-time">{spans ? `${p.month} ${p.day} – ${ep.month} ${ep.day} · ` : ""}{p.time}{tz ? ` (${tz})` : ""}{e.location?.city ? ` · ${e.location.city}` : (e.format === "virtual" ? " · Online" : "")}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const EV_GROUPS = [
  { key: "category", label: "category", ref: "event_categories" },
  { key: "format", label: "format", ref: "event_formats" },
  { key: "topics", label: "topic", ref: "resource_topics" },
  { key: "industries", label: "industry", ref: "industries" },
];

/**
 * The ONE Events directory. `/events` renders it unscoped; a Group's Events tab renders the
 * SAME component with `groupId` set (GRP-DEC-317), which adds `group_id` to the canonical
 * `/events/public` query — the server then requires membership and pins the result to that
 * Group's approved associations, so scoping is authorization, not presentation.
 */
export function EventsDirectory({ groupId = null, groupName = null }) {
  const { references } = useReferences();
  const { user } = useAuth();
  const [view, setView] = useState("grid");
  const [when, setWhen] = useState("upcoming");
  const [source, setSource] = useState("all");
  const [multi, setMulti] = useState({ category: [], format: [], topics: [], industries: [] });
  const [geo, setGeo] = useState({ geoOn: false, center: null, radius: 50 });
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");

  // Default the geo center to the signed-in user's saved location.
  useEffect(() => {
    let ok = true;
    if (user) {
      api.get("/directory/my-location").then(({ data }) => {
        if (ok && data && data.lat != null) {
          setGeo((g) => (g.center ? g : { ...g, center: { lat: data.lat, lng: data.lng, label: data.label || "My location" } }));
        }
      }).catch(() => {});
    }
    return () => { ok = false; };
  }, [user]);

  // Debounce the free-text query so filter changes still fetch immediately.
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const setMultiKey = (key, arr) => setMulti((m) => ({ ...m, [key]: arr }));
  const patchGeo = (p) => setGeo((g) => ({ ...g, ...p }));

  const multiKey = JSON.stringify(multi);
  const localMode = useLocalMode();
  const centerKey = geo.center ? `${geo.center.lat},${geo.center.lng}` : "";

  // Calendar needs every event for the month; grid/list paginate with infinite scroll.
  const calendar = view === "calendar";
  const fetchPage = useCallback(async (offset, signal) => {
    const params = new URLSearchParams();
    params.set("when", calendar ? "all" : when);
    if (multi.category.length) params.set("category", multi.category.join(","));
    if (multi.format.length) params.set("format", multi.format.join(","));
    if (multi.topics.length) params.set("topics", multi.topics.join(","));
    if (multi.industries.length) params.set("industries", multi.industries.join(","));
    if (source !== "all") params.set("source", source);
    if (dq) params.set("q", dq);
    if (geo.geoOn && geo.center) {
      if (isRegionCenter(geo.center)) {
        if (geo.center.granularity === "state") params.set("state", geo.center.state_code);
        else params.set("country", geo.center.country_code);
      } else {
        params.set("lat", geo.center.lat);
        params.set("lng", geo.center.lng);
        params.set("radius", geo.radius);
      }
    }
    if (localMode.localParams) Object.entries(localMode.localParams).forEach(([k, v]) => params.set(k, String(v)));
    if (groupId) params.set("group_id", groupId);
    params.set("limit", calendar ? "500" : "24");
    params.set("offset", String(offset));
    const { data } = await api.get(`/events/public?${params.toString()}`, { signal });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendar, when, source, dq, multiKey, geo.geoOn, geo.radius, centerKey, localMode.localKey, groupId]);

  const resetKey = `${view}|${when}|${source}|${dq}|${multiKey}|${geo.geoOn}|${geo.radius}|${centerKey}|${localMode.localKey}|${groupId || ""}`;
  const { items: events, loadingMore, hasMore, sentinelRef } = useInfiniteScroll({
    fetchPage, resetKey, pageSize: calendar ? 500 : 24,
  });
  const bindImpression = useAnalyticsImpressions("directory_impression");
  const clickTracker = (ev) => (ev.analytics
    ? () => trackResult("directory_result_clicked", ev.analytics) : undefined);

  const chips = useMemo(() => {
    const out = [];
    EV_GROUPS.forEach((g) => (multi[g.key] || []).forEach((v) => out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    if (geo.geoOn && geo.center) out.push({ key: "__geo", value: "geo", label: isRegionCenter(geo.center) ? `In ${geo.center.label}` : `Within ${geo.radius} mi of ${geo.center.label}` });
    return out;
  }, [multi, geo, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") { patchGeo({ geoOn: false }); return; }
    setMultiKey(chip.key, (multi[chip.key] || []).filter((v) => v !== chip.value));
  };
  const clearAll = () => { setMulti({ category: [], format: [], topics: [], industries: [] }); setSource("all"); patchGeo({ geoOn: false }); };
  const hasActive = chips.length > 0 || source !== "all";

  return (
    <div className={groupId ? "" : "mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8"}
         data-testid={groupId ? "group-events-directory" : undefined}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          {groupId ? (
            <>
              <h2 className="text-base font-semibold text-foreground">Events</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Events {groupName ? `shared with ${groupName}` : "shared with this group"}.
                Members-only events stay inside the group.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-[720] tracking-[-0.02em] text-foreground">Events</h1>
              <p className="mt-1 text-muted-foreground">Discover meetups, demo days and workshops across the community.</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} className="rounded-lg border border-border p-1">
            <ToggleGroupItem value="grid" aria-label="Grid view" data-testid="view-grid"><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view" data-testid="view-list"><ListIcon className="h-4 w-4" /></ToggleGroupItem>
            <ToggleGroupItem value="calendar" aria-label="Calendar view" data-testid="view-calendar"><CalendarDays className="h-4 w-4" /></ToggleGroupItem>
          </ToggleGroup>
          {groupId ? null : (
            <DirectoryCreateButton label="Create event" to="/dashboard/events?new=1" testId="events-create-cta" />
          )}
        </div>
      </div>

      {/* Upcoming / Past (GRP-DEC-322) — the Group reads as a mini calendar, so the two
          states are tabs rather than a dropdown. "Upcoming" is the canonical
          `when=upcoming`, which already includes an event happening right now. */}
      {groupId && view !== "calendar" ? (
        <div className="mt-4">
          <ToggleGroup type="single" value={when} onValueChange={(v) => v && setWhen(v)}
                       className="rounded-lg border border-border p-1" aria-label="Date range">
            <ToggleGroupItem value="upcoming" data-testid="group-events-upcoming">Upcoming</ToggleGroupItem>
            <ToggleGroupItem value="past" data-testid="group-events-past">Past</ToggleGroupItem>
          </ToggleGroup>
        </div>
      ) : null}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input placeholder="Search events" value={q} onChange={(e) => setQ(e.target.value)} data-testid="events-search" />
        </div>
        {view !== "calendar" && !groupId && (
          <Select value={when} onValueChange={setWhen}><SelectTrigger className="w-[130px]" data-testid="filter-when"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="upcoming">Upcoming</SelectItem><SelectItem value="past">Past</SelectItem><SelectItem value="all">All dates</SelectItem></SelectContent>
          </Select>
        )}
        <div className="w-[150px]"><MultiSelect label="Any category" options={references.event_categories || []} selected={multi.category} onChange={(a) => setMultiKey("category", a)} testId="filter-category" /></div>
        <div className="w-[140px]"><MultiSelect label="Any format" options={references.event_formats || []} selected={multi.format} onChange={(a) => setMultiKey("format", a)} testId="filter-format" /></div>
        <div className="w-[140px]"><MultiSelect label="Any topic" options={references.resource_topics || []} selected={multi.topics} onChange={(a) => setMultiKey("topics", a)} testId="filter-topics" /></div>
        <div className="w-[140px]"><MultiSelect label="Any industry" options={references.industries || []} selected={multi.industries} onChange={(a) => setMultiKey("industries", a)} testId="filter-industries" /></div>
        <Select value={source} onValueChange={setSource}><SelectTrigger className="w-[150px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Community &amp; Official</SelectItem><SelectItem value="community">Community</SelectItem><SelectItem value="internal">Official</SelectItem></SelectContent>
        </Select>
        <GeoRadiusFilter value={geo} onChange={patchGeo} testId="events-geo" />
      </div>

      {chips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {chips.map((c, i) => (
            <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary" data-testid={`applied-chip-${c.value}`}>
              {c.label}<X className="h-3 w-3" />
            </button>
          ))}
          {hasActive && <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline" data-testid="events-clear-filters">Clear all</button>}
        </div>
      )}

      {events !== null && (
        <div className="mt-4 text-sm text-muted-foreground" data-testid="events-result-count">{events.length} result{events.length !== 1 ? "s" : ""}</div>
      )}

      <div className="mt-6">
        {events === null ? <Loading /> : view === "calendar" ? (
          <MonthCalendar events={events} references={references} />
        ) : events.length === 0 ? (
          <EmptyState icon={Calendar} title="No events found" description="Try adjusting your filters, or check back soon." testId="events-empty" />
        ) : (
          <>
            {view === "grid" ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {events.map((ev) => (
                  <EventCard
                    key={ev.id}
                    ev={ev}
                    references={references}
                    trackRef={bindImpression(ev.analytics)}
                    onTrackClick={clickTracker(ev)}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((ev) => {
                  const p = eventDateParts(ev.start_at);
                  const range = dayRange(ev);
                  const zone = ev.timezone ? (labelFor(references.timezones, ev.timezone) || ev.timezone) : "";
                  const tz = zone ? (zoneLetterAbbr(ev.start_at, zone) || zone) : "";
                  return (
                    <Link key={ev.id} to={`/event/${ev.slug}`} data-testid={`event-public-${ev.id}`} ref={bindImpression(ev.analytics)} onClick={clickTracker(ev)}>
                      <Card className="flex items-center gap-4 border-0 p-4 transition-shadow hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)]">
                        <DateBadge iso={ev.start_at} />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-[640] text-foreground">{ev.title}</h3>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">{ev.format === "virtual" ? <Globe className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}{ev.format === "virtual" ? "Online" : (ev.location?.city || "In person")}</span>
                            <span data-testid="event-list-time">{range ? range : `${p.weekday}, ${p.month} ${p.day}`} · {p.time}{tz ? ` (${tz})` : ""}</span>
                            {ev.category && <Badge className="bg-teal-50 text-teal-700">{labelFor(references.event_categories, ev.category)}</Badge>}
                            {ev.interested_preview && ev.interested_preview.count > 0 && <span className="text-xs">· {ev.interested_preview.count} interested</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="secondary" className="hidden sm:inline-flex" data-testid={`event-list-view-${ev.id}`}>View</Button>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}
            <div ref={sentinelRef} aria-hidden="true" className="h-1" />
            {loadingMore && <div className="mt-6 flex justify-center" data-testid="events-loading-more"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          </>
        )}
      </div>
    </div>
  );
}

export default function Events() {
  return <EventsDirectory />;
}
